import { IncidenceMainType, IncidenceSubTypeEnum } from "@enums/incidences.enum";
import { Responsibility, Severity, IncidenceStatus, IncidenceEventType, AdjustmentType } from "@enums/additional.enum";
import { TransportPrincipalType } from "@enums/transport.enum";
import { AdditionalServiceDto } from "@dtos/additional-services.dto";
import { AdjustmentIncidenceResponseItem } from "./adjustment.item";

export class IncidentRequestDto {
    id: number = 0;
    comments: string = '';
    dont_make_adjustment: boolean = false;
    incidence_main_type: IncidenceMainType | string = IncidenceMainType.OTHER;
    incidence_sub_type: IncidenceSubTypeEnum | string = IncidenceSubTypeEnum.OP_OTHER;
    rating_modification: string = '0.00';
    status: IncidenceStatus | string = IncidenceStatus.NEW;
    responsibility: Responsibility | string = 'NONE';
    severity: Severity | string = Severity.NONE;
    client_zendesk_link: string = '';
    driver_zendesk_link: string = '';
    amount: number = 0;
    driver: IncidentRequestDriverDto = new IncidentRequestDriverDto();
    transport: IncidentRequestTransportDto = new IncidentRequestTransportDto();
    client: IncidentRequestClientDto = new IncidentRequestClientDto();
    responsible: IncidentRequestUserDto = new IncidentRequestUserDto();
    creator: IncidentRequestUserDto = new IncidentRequestUserDto();
    uploaded_documents: any[] = [];
    state: boolean = true;
    created_date: string = '';
    modified_date: string = '';
    notice_driver: boolean = false;
    exclude_by_client_movement_type: boolean = false;
    in_client_blacklist: boolean = false;
    adjustment?: AdjustmentIncidenceResponseItem;
    timeline: IncidentRequestTimelineDto[] = [];

    constructor(data: any = {}) {
        this.copyFrom(data);
    }

    static fromJson(json: any): IncidentRequestDto {
        return new IncidentRequestDto(json);
    }

    toJson(): any {
        return {
            ...this,
            driver: this.driver?.toJson(),
            transport: this.transport?.toJson(),
            client: this.client?.toJson(),
            responsible: this.responsible?.toJson(),
            creator: this.creator?.toJson(),
            timeline: this.timeline?.map(t => t.toJson())
        };
    }

    copyFrom(data: any): void {
        if (!data) return;
        this.id = data.id ?? this.id;
        this.comments = data.comments ?? this.comments;
        this.dont_make_adjustment = data.dont_make_adjustment ?? this.dont_make_adjustment;
        this.incidence_main_type = data.incidence_main_type ?? this.incidence_main_type;
        this.incidence_sub_type = data.incidence_sub_type ?? this.incidence_sub_type;
        this.rating_modification = data.rating_modification ?? this.rating_modification;
        this.status = data.status ?? this.status;
        this.responsibility = data.responsibility ?? this.responsibility;
        this.severity = data.severity ?? this.severity;
        this.client_zendesk_link = data.client_zendesk_link ?? this.client_zendesk_link;
        this.driver_zendesk_link = data.driver_zendesk_link ?? this.driver_zendesk_link;
        this.amount = data.amount ?? this.amount;
        
        if (data.driver) {
            this.driver = (this.driver || new IncidentRequestDriverDto()).copyFrom(data.driver);
        }
        if (data.transport) {
            this.transport = (this.transport || new IncidentRequestTransportDto()).copyFrom(data.transport);
        }
        if (data.client) {
            this.client = (this.client || new IncidentRequestClientDto()).copyFrom(data.client);
        }
        if (data.responsible) {
            this.responsible = (this.responsible || new IncidentRequestUserDto()).copyFrom(data.responsible);
        }
        if (data.creator) {
            this.creator = (this.creator || new IncidentRequestUserDto()).copyFrom(data.creator);
        }

        this.uploaded_documents = data.uploaded_documents ?? this.uploaded_documents;
        this.state = data.state ?? this.state;
        this.created_date = data.created_date ?? this.created_date;
        this.modified_date = data.modified_date ?? this.modified_date;
        this.notice_driver = data.notice_driver ?? this.notice_driver;
        this.exclude_by_client_movement_type = data.exclude_by_client_movement_type ?? this.exclude_by_client_movement_type;
        this.in_client_blacklist = data.in_client_blacklist ?? this.in_client_blacklist;

        if(data.adjustment){
            this.adjustment = data.adjustment;
        }else{
            if(!this.adjustment?.id){
                this.adjustment = new AdjustmentIncidenceResponseItem();
                if(this.incidence_main_type === IncidenceMainType.DRIVER_RECOGNITION){
                    this.adjustment.adjustment_type = AdjustmentType.BONUS;
                }
            }
        }


      //  this.adjustment = data.adjustment ?? this.adjustment;

        if (Array.isArray(data.timeline)) {
            this.timeline = data.timeline.map((t: any) => new IncidentRequestTimelineDto(t));
        }
    }
}

export class IncidentRequestDriverDto {
    id: number = 0;
    name: string = '';
    last_name: string = '';

    constructor(data: any = {}) {
        this.copyFrom(data);
    }

    toJson(): any { return { ...this }; }

    copyFrom(data: any): this {
        if (!data) return this;
        this.id = data.id ?? this.id;
        this.name = data.name ?? this.name;
        this.last_name = data.last_name ?? this.last_name;
        return this;
    }
}

export class IncidentRequestUserDto {
    id: number = 0;
    name: string = '';
    last_name: string = '';

    constructor(data: any = {}) {
        this.copyFrom(data);
    }

    toJson(): any { return { ...this }; }

    copyFrom(data: any): this {
        if (!data) return this;
        this.id = data.id ?? this.id;
        this.name = data.name ?? this.name;
        this.last_name = data.last_name ?? this.last_name;
        return this;
    }
}

export class IncidentRequestClientDto {
    id: number = 0;
    name: string = '';
    logo: string = '';
    parent_name: string | null = null;

    constructor(data: any = {}) {
        this.copyFrom(data);
    }

    toJson(): any { return { ...this }; }

    copyFrom(data: any): this {
        if (!data) return this;
        this.id = data.id ?? this.id;
        this.name = data.name ?? this.name;
        this.logo = data.logo ?? this.logo;
        this.parent_name = data.parent_name ?? this.parent_name;
        return this;
    }
}

export class IncidentRequestTransportDto {
    id: number = 0;
    transport_principal_type: TransportPrincipalType | string = TransportPrincipalType.SIMPLE_MOVEMENT;
    transport_status: string = '';
    comment: string = '';
    reference_number: string = '';
    is_blocked: boolean = false;
    is_mission_blocked: boolean = false;
    is_express: boolean = false;
    invoiced: boolean = false;
    cancelled_comment: string = '';
    cancelled_reason: string | null = null;
    kilometers: string = '0.00';
    price: string | null = '0.00';
    duration: number = 0;
    created_date: string = '';
    modified_date: string = '';
    client: IncidentRequestClientDto | null = null;
    admin: IncidentRequestUserDto | null = null;
    driver: IncidentRequestUserDto | null = null;
    tags: number[] = [];
    vehicle: IncidentRequestVehicleDto | null = null;
    legs: any[] = []; // Simplified for this nested DTO
    additional_services: AdditionalServiceDto[] = [];
    origin_address: string = '';
    destination_address: string = '';
    start_date: string | null = null;
    end_date: string | null = null;
    expected_start_date: string | null = null;
    expected_end_date: string | null = null;
    related_transport_id: number | null = null;

    constructor(data: any = {}) {
        this.copyFrom(data);
    }

    toJson(): any {
        return {
            ...this,
            client: this.client?.toJson(),
            admin: this.admin?.toJson(),
            driver: this.driver?.toJson(),
            vehicle: this.vehicle?.toJson(),
            is_express: this.is_express,
            additional_services: this.additional_services
        };
    }

    copyFrom(data: any): this {
        if (!data) return this;
        this.id = data.id ?? this.id;
        this.transport_principal_type = data.transport_principal_type ?? this.transport_principal_type;
        this.transport_status = data.transport_status ?? this.transport_status;
        this.comment = data.comment ?? this.comment;
        this.reference_number = data.reference_number ?? this.reference_number;
        this.is_blocked = data.is_blocked ?? this.is_blocked;
        this.is_mission_blocked = data.is_mission_blocked ?? this.is_mission_blocked;
        this.is_express = data.is_express ?? this.is_express;
        this.invoiced = data.invoiced ?? this.invoiced;
        this.cancelled_comment = data.cancelled_comment ?? this.cancelled_comment;
        this.cancelled_reason = data.cancelled_reason ?? this.cancelled_reason;
        this.kilometers = data.kilometers ?? this.kilometers;
        this.price = data.price ?? this.price;
        this.duration = data.duration ?? this.duration;
        this.created_date = data.created_date ?? this.created_date;
        this.modified_date = data.modified_date ?? this.modified_date;
        
        if (data.client) this.client = (this.client || new IncidentRequestClientDto()).copyFrom(data.client);
        if (data.admin) this.admin = (this.admin || new IncidentRequestUserDto()).copyFrom(data.admin);
        if (data.driver) this.driver = (this.driver || new IncidentRequestUserDto()).copyFrom(data.driver);
        if (data.vehicle) this.vehicle = (this.vehicle || new IncidentRequestVehicleDto()).copyFrom(data.vehicle);
        
        this.tags = data.tags ?? this.tags;
        this.legs = data.legs ?? this.legs;
        this.additional_services = data.additional_services ?? this.additional_services;
        this.origin_address = data.origin_address ?? this.origin_address;
        this.destination_address = data.destination_address ?? this.destination_address;
        this.start_date = data.start_date ?? this.start_date;
        this.end_date = data.end_date ?? this.end_date;
        this.expected_start_date = data.expected_start_date ?? this.expected_start_date;
        this.expected_end_date = data.expected_end_date ?? this.expected_end_date;
        this.related_transport_id = data.related_transport_id ?? this.related_transport_id;
        return this;
    }
}

export class IncidentRequestVehicleDto {
    id: number = 0;
    license_plate: string = '';
    brand: string = '';
    model: string = '';
    fuel_type: string = '';
    size: string = '';
    body_type: string = '';
    insurance_date_start: string = '';
    insurance_date_end: string = '';
    image: string = '';
    created_date: string | null = null;
    modified_date: string | null = null;

    constructor(data: any = {}) {
        this.copyFrom(data);
    }

    toJson(): any { return { ...this }; }

    copyFrom(data: any): this {
        if (!data) return this;
        this.id = data.id ?? this.id;
        this.license_plate = data.license_plate ?? this.license_plate;
        this.brand = data.brand ?? this.brand;
        this.model = data.model ?? this.model;
        this.fuel_type = data.fuel_type ?? this.fuel_type;
        this.size = data.size ?? this.size;
        this.body_type = data.body_type ?? this.body_type;
        this.insurance_date_start = data.insurance_date_start ?? this.insurance_date_start;
        this.insurance_date_end = data.insurance_date_end ?? this.insurance_date_end;
        this.image = data.image ?? this.image;
        this.created_date = data.created_date ?? this.created_date;
        this.modified_date = data.modified_date ?? this.modified_date;
        return this;
    }
}

export class IncidentRequestTimelineDto {
    id: number = 0;
    title: string = '';
    description: string = '';
    type: IncidenceEventType= IncidenceEventType.OPENED;
    timestamp: string = '';
    creator: { id: number; name: string } | null = null;

    constructor(data: any = {}) {
        this.copyFrom(data);
    }

    toJson(): any { return { ...this }; }

    copyFrom(data: any): this {
        if (!data) return this;
        this.id = data.id ?? this.id;
        this.title = data.title ?? this.title;
        this.description = data.description ?? this.description;
        this.type = data.type ?? this.type;
        this.timestamp = data.timestamp ?? this.timestamp;
        this.creator = data.creator ?? this.creator;
        return this;
    }
}
